<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { loadFillerConfig } from './lib/data.js';
import {
  calculateCsa,
  calculateDepositionPerLength,
  calculateFillerToPurchase,
} from './lib/logic.js';
import { UNIT_SYSTEMS, UNIT_METADATA } from './lib/units.js';
const plainIcons = {
  surfacing: new URL('../assets/plain/surfacing.png', import.meta.url).href,
  square_groove: new URL('../assets/plain/square-groove.png', import.meta.url).href,
  outside_corner: new URL('../assets/plain/outside-corner.png', import.meta.url).href,
  fillet: new URL('../assets/plain/fillet.png', import.meta.url).href,
  single_bevel: new URL('../assets/plain/single-bevel.png', import.meta.url).href,
  single_v: new URL('../assets/plain/single-v.png', import.meta.url).href,
  single_j: new URL('../assets/plain/single-j.png', import.meta.url).href,
  single_u: new URL('../assets/plain/single-u.png', import.meta.url).href,
};
const linedImages = {
  surfacing: new URL('../assets/lined/surfacing.png', import.meta.url).href,
  square_groove: new URL('../assets/lined/square-groove.png', import.meta.url).href,
  outside_corner: new URL('../assets/lined/outside-corner.png', import.meta.url).href,
  fillet_plus: new URL('../assets/lined/fillet-plus.png', import.meta.url).href,
  fillet_minus: new URL('../assets/lined/fillet-minus.png', import.meta.url).href,
  single_bevel: new URL('../assets/lined/single-bevel.png', import.meta.url).href,
  single_v: new URL('../assets/lined/single-v.png', import.meta.url).href,
  single_j: new URL('../assets/lined/single-j.png', import.meta.url).href,
  single_u: new URL('../assets/lined/single-u.png', import.meta.url).href,
};
const config = ref(null);
const unitSystem = ref(UNIT_SYSTEMS.imperial);
const step = ref('menu');
const selectedJointId = ref(null);
const jointInputs = reactive({});
const frePositive = ref(true);
const weldLength = ref('');
const selectedMaterialId = ref(null);
const selectedTransferId = ref(null);
const loading = ref(true);
onMounted(async () => {
  const data = await loadFillerConfig();
  config.value = data;
  selectedMaterialId.value = data.materials[0]?.id ?? null;
  selectedTransferId.value = data.transfers[0]?.id ?? null;
  loading.value = false;
});
const materials = computed(() => config.value?.materials ?? []);
const transfers = computed(() => config.value?.transfers ?? []);
const joints = computed(() => config.value?.joints ?? []);
const currentJoint = computed(
  () => joints.value.find((joint) => joint.id === selectedJointId.value) ?? null
);
const currentMaterial = computed(
  () => materials.value.find((material) => material.id === selectedMaterialId.value) ?? null
);
const disallowedTransfers = computed(() => {
  if (!currentMaterial.value) return new Set();
  return new Set(currentMaterial.value.disallowed_transfers ?? []);
});
const transferGroups = computed(() => {
  const groups = new Map();
  for (const transfer of transfers.value) {
    if (!groups.has(transfer.group)) {
      groups.set(transfer.group, []);
    }
    groups.get(transfer.group).push(transfer);
  }
  return groups;
});
watch(
  currentMaterial,
  (material) => {
    if (!material) return;
    const invalid = material.disallowed_transfers?.includes(selectedTransferId.value);
    if (invalid) {
      const allowed = transfers.value.find(
        (t) => !(material.disallowed_transfers || []).includes(t.id)
      );
      selectedTransferId.value = allowed?.id ?? null;
    }
    if (!selectedTransferId.value && transfers.value.length) {
      selectedTransferId.value = transfers.value[0].id;
    }
  },
  { immediate: true }
);
const currentTransfer = computed(
  () => transfers.value.find((transfer) => transfer.id === selectedTransferId.value) ?? null
);
const csaValue = computed(() => {
  if (!currentJoint.value) return Number.NaN;
  return calculateCsa(currentJoint.value.id, jointInputs, { frePositive: frePositive.value });
});
const depositionPerLength = computed(() => {
  if (!currentMaterial.value) return Number.NaN;
  return calculateDepositionPerLength(csaValue.value, currentMaterial.value, unitSystem.value);
});
const weldLengthNumber = computed(() => {
  const num = Number.parseFloat(weldLength.value);
  return Number.isFinite(num) ? num : Number.NaN;
});
const fillerToPurchase = computed(() => {
  if (!currentTransfer.value) return Number.NaN;
  return calculateFillerToPurchase(
    depositionPerLength.value,
    weldLengthNumber.value,
    currentTransfer.value.efficiency,
    unitSystem.value
  );
});
const unitMeta = computed(() => UNIT_METADATA[unitSystem.value]);
const linedImageSource = computed(() => {
  if (!currentJoint.value) return '';
  if (currentJoint.value.id === 'fillet') {
    return frePositive.value ? linedImages.fillet_plus : linedImages.fillet_minus;
  }
  return linedImages[currentJoint.value.id] ?? '';
});
function formatNumber(value, digits = 2) {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
function formatCsa(value) {
  return formatNumber(value, 4);
}
function startJoint(jointId) {
  selectedJointId.value = jointId;
  frePositive.value = true;
  for (const key of Object.keys(jointInputs)) {
    delete jointInputs[key];
  }
  const joint = joints.value.find((item) => item.id === jointId);
  if (joint) {
    for (const field of joint.fields) {
      jointInputs[field.id] = '';
    }
  }
  step.value = 'joint';
}
function goToLengthView() {
  weldLength.value = weldLength.value || '';
  step.value = 'length';
}
function backToMenu() {
  step.value = 'menu';
  selectedJointId.value = null;
}
function backToJoint() {
  step.value = 'joint';
}
const fieldDisplayList = computed(() => {
  if (!currentJoint.value) return [];
  return currentJoint.value.fields.map((field) => ({
    label: field.label,
    value: jointInputs[field.id] ?? '',
  }));
});
const orderedTransferGroups = computed(() => {
  const order = ['gmaw', 'stick_fcaw'];
  return order
    .map((key) => ({ key, transfers: transferGroups.value.get(key) || [] }))
    .filter((group) => group.transfers.length > 0);
});
function selectMaterial(id) {
  selectedMaterialId.value = id;
}
function selectTransfer(id, disabled) {
  if (disabled) return;
  selectedTransferId.value = id;
}
const loadingMessage = computed(() => (loading.value ? 'Loading…' : ''));
</script>
<template>
  <div class="hb-container hb-container-wide">
    <h1 class="hb-page-title">Filler Metal Needed For Job</h1>
    <p class="picker-help">Select a weld joint to calculate cross-sectional area.</p>
    <div class="unit-toggle">
      <button
        class="unit-button"
        :class="{ active: unitSystem === UNIT_SYSTEMS.imperial }"
        type="button"
        @click="unitSystem = UNIT_SYSTEMS.imperial"
      >
        Imperial (SAE)
      </button>
      <button
        class="unit-button"
        :class="{ active: unitSystem === UNIT_SYSTEMS.metric }"
        type="button"
        @click="unitSystem = UNIT_SYSTEMS.metric"
      >
        Metric
      </button>
    </div>
    <div v-if="loading">{{ loadingMessage }}</div>
    <template v-else>
      <div v-if="step === 'menu'" class="view-section">
        <div class="joint-grid">
          <button
            v-for="joint in joints"
            :key="joint.id"
            type="button"
            class="joint-card"
            @click="startJoint(joint.id)"
          >
            <img :src="plainIcons[joint.id] || linedImages[joint.id]" :alt="joint.label" />
            <span>{{ joint.label }}</span>
          </button>
        </div>
      </div>
      <div v-else-if="step === 'joint' && currentJoint" class="view-section">
        <button type="button" class="link-button" @click="backToMenu">Back to joints</button>
        <p class="picker-help joint-step-help">
          Enter joint dimensions to calculate cross-sectional area.
        </p>
        <h2 class="hb-title">{{ currentJoint.label }}</h2>
        <div class="hb-form">
          <div v-for="field in currentJoint.fields" :key="field.id" class="hb-form-row">
            <div class="hb-form-label">{{ field.label }}:</div>
            <input
              v-model="jointInputs[field.id]"
              class="hb-input"
              type="number"
              inputmode="decimal"
              placeholder="Enter value"
            />
          </div>
        </div>
        <div v-if="currentJoint.options?.fre_polarity_toggle" class="segment-group">
          <button
            type="button"
            class="segment-button"
            :class="{ active: frePositive }"
            @click="frePositive = true"
          >
            +FRE
          </button>
          <button
            type="button"
            class="segment-button"
            :class="{ active: !frePositive }"
            @click="frePositive = false"
          >
            -FRE
          </button>
        </div>
        <div class="result-card" aria-live="polite">
          <div><strong>CSA:</strong> {{ formatCsa(csaValue) }} {{ unitMeta.csaUnit }}</div>
        </div>
        <div class="joint-actions">
          <button
            type="button"
            class="hb-button-accent"
            :disabled="!Number.isFinite(csaValue)"
            @click="goToLengthView"
          >
            Length and Materials
          </button>
        </div>
        <div class="joint-image">
          <img :src="linedImageSource" :alt="`${currentJoint.label} diagram`" />
        </div>
        <div class="joint-back-footer">
          <button
            type="button"
            class="hb-button hb-button-black hb-button-small"
            @click="backToMenu"
          >
            Back
          </button>
        </div>
      </div>
      <div v-else-if="step === 'length' && currentJoint" class="view-section">
        <button type="button" class="link-button" @click="backToJoint">Back</button>
        <p class="picker-help joint-step-help">
          Enter weld length and process details to estimate filler needs.
        </p>
        <h2 class="hb-title">Length and Materials</h2>
        <div class="section-label">Material Type</div>
        <div class="segment-group">
          <button
            v-for="material in materials"
            :key="material.id"
            type="button"
            class="segment-button"
            :class="{ active: selectedMaterialId === material.id }"
            @click="selectMaterial(material.id)"
          >
            {{ material.label }}
          </button>
        </div>
        <div class="section-label">Mode of Transfer</div>
        <div v-for="group in orderedTransferGroups" :key="group.key" class="segment-group">
          <button
            v-for="transfer in group.transfers"
            :key="transfer.id"
            type="button"
            class="segment-button"
            :class="{
              active: selectedTransferId === transfer.id,
              disabled: disallowedTransfers.has(transfer.id),
            }"
            :disabled="disallowedTransfers.has(transfer.id)"
            @click="selectTransfer(transfer.id, disallowedTransfers.has(transfer.id))"
          >
            {{ transfer.label }}
          </button>
        </div>
        <div class="hb-form" style="margin-top: 20px">
          <div class="hb-form-row">
            <div class="hb-form-label">{{ unitMeta.weldLengthLabel }}</div>
            <input
              v-model="weldLength"
              class="hb-input"
              type="number"
              inputmode="decimal"
              :placeholder="unitMeta.weldLengthPlaceholder"
            />
          </div>
        </div>
        <div class="result-card orange" aria-live="polite">
          <div><strong>CSA:</strong> {{ formatCsa(csaValue) }} {{ unitMeta.csaUnit }}</div>
          <div class="result-text">
            Approx. Depo Wt. Req: {{ formatNumber(depositionPerLength) }}
            {{ unitMeta.depositionUnit }}
          </div>
          <div class="result-text">
            Depo Efficiency: {{ formatNumber(currentTransfer?.efficiency, 2) }}
          </div>
          <div class="result-text">
            <strong>Approx. Filler to Purchase:</strong>
            {{ formatNumber(fillerToPurchase) }} {{ unitMeta.fillerUnit }}
          </div>
        </div>
        <div class="joint-summary">
          <div class="joint-image">
            <img :src="linedImageSource" :alt="`${currentJoint.label} diagram`" />
          </div>
          <div class="value-list">
            <div v-for="item in fieldDisplayList" :key="item.label" class="value-row">
              <span>{{ item.label }}:</span>
              <span>{{ item.value || '—' }}</span>
            </div>
          </div>
        </div>
        <div class="joint-back-footer">
          <button
            type="button"
            class="hb-button hb-button-black hb-button-small"
            @click="backToJoint"
          >
            Back
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
<style scoped>
.unit-toggle {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 8px 0 20px;
  flex-wrap: wrap;
  padding: 0 12px;
}
.unit-button {
  border: 2px solid var(--hb-accent);
  border-radius: 999px;
  padding: 10px 0;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  flex: 1 1 160px;
  max-width: 220px;
  text-align: center;
  color: #000;
}
.unit-button.active {
  background: var(--hb-accent);
  color: #000;
  border-color: var(--hb-accent);
}
.unit-button:hover {
  filter: brightness(0.96);
}
.hb-button,
.hb-button-accent {
  background: var(--hb-accent);
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 700;
  cursor: pointer;
}
.hb-button-black {
  background: #000;
  color: #fff;
}
.hb-button:hover,
.hb-button-accent:hover {
  filter: brightness(0.95);
}
.hb-button[disabled],
.hb-button-accent[disabled] {
  opacity: 0.5;
  cursor: default;
}
.view-section {
  display: grid;
  gap: 18px;
}
.joint-step-help {
  margin-top: -6px;
}
.joint-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}
.joint-card {
  border: 1px solid var(--hb-border);
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  cursor: pointer;
  display: grid;
  gap: 8px;
  justify-items: center;
  text-align: center;
  transition: box-shadow 0.15s ease;
}
.joint-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}
.joint-card img {
  max-width: 100%;
  height: 80px;
  object-fit: contain;
}
.segment-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.segment-button {
  border: 1px solid var(--hb-border);
  border-radius: 999px;
  padding: 8px 16px;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
}
.segment-button.active {
  background: #000;
  color: #fff;
  border-color: #000;
}
.segment-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.result-card {
  border: 1px solid var(--hb-border);
  border-radius: 6px;
  padding: 14px 16px;
  background: #fafafa;
  display: grid;
  gap: 6px;
}
.result-card.orange {
  background: #f37021;
  color: #000;
}
.joint-actions {
  display: flex;
  justify-content: space-between;
}
.joint-image {
  display: flex;
  justify-content: center;
}
.joint-image img {
  max-width: 100%;
  width: 280px;
  object-fit: contain;
}
.link-button {
  border: none;
  background: none;
  color: var(--hb-accent);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}
.section-label {
  font-weight: 600;
}
.joint-summary {
  display: grid;
  gap: 12px;
}
.value-list {
  border: 1px solid var(--hb-border);
  border-radius: 6px;
  padding: 12px;
  display: grid;
  gap: 6px;
}
.value-row {
  display: flex;
  justify-content: space-between;
}
@media (min-width: 640px) {
  .joint-summary {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
}
</style>
